import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  MessageSquare,
  Settings,
  Database,
  Image,
  Printer,
  Trash2,
  Layers,
  Users,
  LogOut,
  Shield,
  Contact,
  Bot,
  ListFilter,
  BarChart2,
Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";

export default function Sidebar({ stats = {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(() => {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr) : null;
  });
  
  useEffect(() => {
    const checkUser = () => {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user?.id !== currentUser?.id || user?.role !== currentUser?.role || 
            JSON.stringify(user?.permissions) !== JSON.stringify(currentUser?.permissions)) {
          setCurrentUser(user);
        }
      }
    };
    
    checkUser();
    window.addEventListener("storage", checkUser);
    return () => window.removeEventListener("storage", checkUser);
  }, [location.pathname, currentUser?.id, currentUser?.role, currentUser?.permissions]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setCurrentUser(null);
    navigate("/login");
  };
  
  const isAdmin = currentUser?.role === "admin";
  
  const hasPermission = (permission) => {
    if (isAdmin) return true;
    const userPermissions = currentUser?.permissions || [];
    return userPermissions.includes(permission) || userPermissions.includes("all");
  };

  const navItems = [
    {
      icon: MessageSquare,
      label: "Support",
      path: "/dashboard",
      permission: "support",
    },
    {
      icon: Database,
      label: "Orders",
      path: "/orders",
      permission: "orders",
    },
    {
      icon: Image,
      label: "Images",
      path: "/images",
      permission: "images",
    },
    {
      icon: Trash2,
      label: "Trash",
      path: "/trash",
      permission: "images",
    },
    {
      icon: Layers,
      label: "Templates",
      path: "/templates",
      permission: "templates",
    },
    {
      icon: BarChart2,
      label: "Analytics",
      path: "/analytics",
      permission: "orders",
    },
    {
      icon: Printer,
      label: "Production",
      path: "/production",
      permission: "production",
    },
    {
      icon: Package,
      label: "Outsourced Work",
      path: "/outsourced",
      permission: "production",
    },
    {
      icon: Contact,
      label: "Contacts",
      path: "/contacts",
      permission: "support",  // Anyone with support permission can see contacts
    },
  ].filter(item => hasPermission(item.permission));

  const bottomItems = [
    ...(isAdmin ? [{
      icon: Bot,
      label: "AI Agent",
      path: "/ai-agent",
    }] : []),
    ...(isAdmin ? [{
      icon: ListFilter,
      label: "Dropdowns",
      path: "/dropdown-manager",
    }] : []),
    ...(isAdmin ? [{
      icon: Users,
      label: "Users",
      path: "/admin/users",
    }] : []),
    ...(isAdmin ? [{
      icon: Settings,
      label: "Settings",
      path: "/settings",
    }] : []),
  ];

  return (
    <aside
      className="w-16 bg-[#5990DC] flex flex-col items-center py-4 shrink-0"
      data-testid="main-sidebar"
    >
      {/* Current User Display */}
      {currentUser && (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="mb-4 flex flex-col items-center cursor-pointer group">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-base shadow-lg ${
                  isAdmin ? 'bg-amber-500' : 'bg-[#E8EDD4] text-[#5990DC]'
                }`}>
                  {currentUser.name?.charAt(0).toUpperCase() || "U"}
                </div>
                <div className="mt-1.5 text-[11px] text-white font-medium text-center max-w-[56px] truncate">
                  {currentUser.name?.split(' ')[0] || "User"}
                </div>
                {isAdmin && (
                  <Shield className="h-3.5 w-3.5 text-amber-300 mt-0.5" strokeWidth={2.5} />
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="flex flex-col gap-1">
              <span className="font-medium">{currentUser.name}</span>
              <span className="text-xs text-muted-foreground">{currentUser.email}</span>
              <span className="text-xs capitalize text-primary">{currentUser.role}</span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      
      <Separator className="bg-white/30 w-8 mb-4" />

      {/* Navigation */}
      <nav className="flex-1 flex flex-col items-center gap-2">
        <TooltipProvider delayDuration={0}>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;

            return (
              <Tooltip key={item.path}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`
                      relative h-11 w-11 rounded-xl transition-all
                      ${
                        isActive
                          ? "bg-[#E8EDD4] text-[#5990DC] shadow-md"
                          : "text-white hover:text-[#5990DC] hover:bg-[#E8EDD4]"
                      }
                    `}
                    onClick={() => navigate(item.path)}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="h-6 w-6" strokeWidth={2} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {item.label}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </TooltipProvider>
      </nav>

      <Separator className="bg-white/30 w-8 mb-4" />

      {/* Bottom Navigation */}
      <TooltipProvider delayDuration={0}>
        {bottomItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;

          return (
            <Tooltip key={item.path}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`
                    h-11 w-11 rounded-xl transition-all mb-1
                    ${
                      isActive
                        ? "bg-[#E8EDD4] text-[#5990DC] shadow-md"
                        : "text-white hover:text-[#5990DC] hover:bg-[#E8EDD4]"
                    }
                  `}
                  onClick={() => navigate(item.path)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <Icon className="h-6 w-6" strokeWidth={2} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">{item.label}</TooltipContent>
            </Tooltip>
          );
        })}
        
        {/* Logout Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11 rounded-xl transition-all text-white hover:text-red-500 hover:bg-red-50 mt-2"
              onClick={handleLogout}
              data-testid="nav-logout"
            >
              <LogOut className="h-6 w-6" strokeWidth={2} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            Logout {currentUser?.name && `(${currentUser.name})`}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </aside>
  );
}
